<template>
  <div class="keyword-search-bar">
    <BaseIcon name="search" class="keyword-search-bar__icon" />
    <input
      class="keyword-search-bar__input"
      :class="{
        'has-gray-background': hasGrayBackground,
      }"
      :name="name"
      id="keywordSearchBar"
      :type="type"
      :value="value"
      :placeholder="placeholder"
      @keydown.enter="searchKeyword"
    />
    <div class="keyword-search-bar__results">
      <div class="keyword-search-bar__result-count" v-if="isSelectedKeywordManual && keywordOccurrences">
        {{ keywordIndex }}/{{ keywordOccurrences }}
      </div>
      <div class="keyword-search-bar__navigation-arrows" v-if="isSelectedKeywordManual">
        <div v-if="keywordOccurrences === 0" class="keyword-search-bar__no-results">0/0</div>
        <BaseRoundButton
          v-show="keywordOccurrences > 0"
          icon="chevron-left"
          @click.native.stop="goToPrevious"
          :disabled="keywordIndex === 1"
          is-transparent
        />
        <BaseRoundButton
          v-show="keywordOccurrences > 0"
          icon="chevron-right"
          @click.native.stop="goToNext"
          :disabled="keywordIndex === keywordOccurrences"
          is-transparent
        />
        <BaseRoundButton icon="x" is-transparent @click.native.stop="clearSearch" />
      </div>
    </div>
  </div>
</template>

<script>
import { bus } from '@/main';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import KeywordNavigationMixin from '@/mixins/keyword-navigation-mixin';

import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'KeywordSearchBar',
  components: {
    BaseIcon,
    BaseRoundButton,
  },
  mixins: [KeywordNavigationMixin],
  props: {
    name: String,
    type: {
      type: String,
      default: 'text',
    },
    // TODO
    placeholder: {
      type: String,
      default: 'Search',
    },
    hasGrayBackground: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      value: null,
    };
  },
  computed: {
    ...mapGetters('opportunityKeywordSearch', [
      'keywordLocations',
      'keywordIndex',
      'keywordOccurrences',
      'selectedKeyword',
    ]),
    ...mapGetters('currentDocument', ['currentPageNumber']),
    ...mapGetters('opportunityDocuments', ['opportunityDocuments']),
    ...mapGetters('opportunityDocumentsPdfEngine', ['searchResult']),
    isSelectedKeywordManual() {
      return this.selectedKeyword && this.selectedKeyword.id == null;
    },
  },
  methods: {
    ...mapActions('opportunityKeywordSearch', [
      'setSelectedKeyword',
      'findManualKeywordLocations',
      'resetSelectedKeyword',
    ]),
    ...mapActions('opportunityDocumentsPdfEngine', ['searchContent']),
    async searchKeyword(event) {
      if (event.key === 'Enter' || event.keyCode === 13) {
        if (event.target.value === '') {
          this.clearSearch();
          return;
        }
        const labelTagMenu = document.querySelector('.label-tag-menu');
        if (labelTagMenu) return;

        // The value has not change, so user want to goToNext occurence
        if (this.value === event.target.value) {
          this.goToNext();
        } else {
          // There is no value yet, reset value and occurence
          this.value = event.target.value;
          const query = this.value;

          if (this.selectedKeyword !== null) this.lowlightKeywords(this.selectedKeyword);

          const opportunityId = this.opportunityDocument.opportunityId;
          try {
            await this.searchContent({ opportunityId, query });
          } catch (error) {
            // e.g. 409 INDEXING_IN_PROGRESS right after an upload: the toast says so (error.code.*)
            this.value = null;
            this.$toast.error(error);
            return;
          }
          await this.findManualKeywordLocations({ searchResult: this.searchResult });

          // Adds count from every document in the opportunity
          let count = this.keywordLocations.reduce((sum, obj) => sum + obj.count, 0);

          await this.resetSelectedKeyword();
          const keyword = {
            value: query
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, ''),
            id: null,
          };
          await this.setSelectedKeyword({
            keyword,
            occurrences: count,
          });

          bus.$emit('manual-kw-search');
          this.firstOccurrence({
            keyword,
            locations: this.keywordLocations,
          });
        }
      }
    },
    clearSearch() {
      this.resetHighlightKeywords();
      this.resetSelectedKeyword();
      this.value = null;
    },
    goToNext() {
      this.nextOccurrence({ keyword: this.selectedKeyword, locations: this.keywordLocations });
    },
    goToPrevious() {
      this.previousOccurrence({ keyword: this.selectedKeyword, locations: this.keywordLocations });
    },
  },
  beforeDestroy() {
    this.resetHighlightKeywords();
    this.resetSelectedKeyword();
    this.value = null;
  },
  watch: {
    selectedKeyword() {
      if (this.selectedKeyword && this.selectedKeyword.id !== null) {
        this.value = null;
      }
    },
  },
};
</script>

<style lang="scss">
.keyword-search-bar {
  position: relative;
  width: calc(100% - 6px);
  background: $color-neutral-pale !important;
  border-radius: $border-radius-large !important;
  border: 1px solid transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 16px;
  height: 36px;
  gap: 8px;
  margin: 2px;

  &__icon {
    flex-grow: 0 1 auto;
    height: 16px;
    width: 16px;
    min-width: 16px;
    max-width: 16px;
    display: flex;
  }

  &__input {
    display: flex;
    background: transparent;
    outline: none;
    flex-grow: 1 !important;
    align-items: center;
    border: 1px solid transparent;
    min-width: 20px;
  }

  input::placeholder {
    font-style: normal;
  }

  &__results {
    display: flex;
    align-items: center;
    flex-grow: 0 1 auto;
    max-width: min-content;
  }

  &__result-count {
    font-size: 14px;
    color: $color-neutral;
  }

  &__navigation-arrows {
    align-items: center;
    display: flex;

    .base-round-button {
      height: 32px !important;
      width: 32px !important;
    }
  }

  &__no-results {
    font-size: 14px;
    color: $color-neutral;
    flex-wrap: nowrap;
    display: flex;
    max-width: min-content;
    padding-right: 8px;
  }

  &:focus-within {
    outline: $color-accent solid 1px;
  }
}
</style>
